import { Effect, Match, Option, pipe, Schema, SchemaGetter, SchemaIssue } from 'effect';

import {
  AddressRequirementError,
  CartItemInsufficientStockError,
  CartItemNotAvailableError,
  CartItemQuantityNotInRangeError,
  CartItemVariantNotAvailableError,
  CurrencyCodeNotValidError,
  OrderCartItemsImmutableError,
  OrderInvalidStateTransitionError,
  OrderNotEmptyError,
  PaymentMethodEligibilityError,
  ProductEligibilityError,
  ProductVariantCodeNotExistError,
  ProductVariantNotExistError,
  ProductVariantNotSufficientError,
  ShippingMethodEligibilityError,
  ShippingMethodNotAvailableError,
  UnknownViolationError,
  ViolationCode,
} from '../data/error/orderViolation';

/**
 * API Violation Structure (from Sylius)
 */
const ApiViolation = Schema.Struct({
  propertyPath: Schema.optional(Schema.String),
  message: Schema.optional(Schema.String),
  code: Schema.optional(Schema.Union([Schema.String, Schema.Null])),
});

type ApiViolation = Schema.Schema.Type<typeof ApiViolation>;

export type OrderViolationFromApi = Schema.Schema.Type<typeof OrderViolationFromApi>;

/**
 * Transform a single API violation into a typed OrderViolationError
 */
export const OrderViolationFromApi = ApiViolation.pipe(
  Schema.decodeTo(
    Schema.Union([
      PaymentMethodEligibilityError,
      ProductEligibilityError,
      ShippingMethodEligibilityError,
      ShippingMethodNotAvailableError,
      AddressRequirementError,
      CurrencyCodeNotValidError,
      CartItemNotAvailableError,
      CartItemInsufficientStockError,
      CartItemQuantityNotInRangeError,
      CartItemVariantNotAvailableError,
      OrderNotEmptyError,
      ProductVariantCodeNotExistError,
      ProductVariantNotExistError,
      ProductVariantNotSufficientError,
      OrderInvalidStateTransitionError,
      OrderCartItemsImmutableError,
      UnknownViolationError,
    ]),
    {
      decode: SchemaGetter.transformEffect((violation) => {
        const code = violation.code;

        if (!code || typeof code !== 'string') {
          return Effect.fail(
            new SchemaIssue.InvalidValue({
              message: 'Violation code is required and must be a string',
            })
          );
        }

        const base = {
          propertyPath: violation.propertyPath,
          message: violation.message,
        };

        return pipe(
          code,
          Match.value,
          Match.when(ViolationCode.PaymentMethodEligibility, (matchedCode) =>
            Effect.succeed(new PaymentMethodEligibilityError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ProductEligibility, (matchedCode) =>
            Effect.succeed(new ProductEligibilityError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ShippingMethodEligibility, (matchedCode) =>
            Effect.succeed(new ShippingMethodEligibilityError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ShippingMethodNotAvailable, (matchedCode) =>
            Effect.succeed(new ShippingMethodNotAvailableError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.AddressRequirement, (matchedCode) =>
            Effect.succeed(new AddressRequirementError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.CurrencyCodeNotValid, (matchedCode) =>
            Effect.succeed(new CurrencyCodeNotValidError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.CartItemNotAvailable, (matchedCode) =>
            Effect.succeed(new CartItemNotAvailableError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.CartItemInsufficientStock, (matchedCode) =>
            Effect.succeed(new CartItemInsufficientStockError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.CartItemQuantityNotInRange, (matchedCode) =>
            Effect.succeed(new CartItemQuantityNotInRangeError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.CartItemVariantNotAvailable, (matchedCode) =>
            Effect.succeed(new CartItemVariantNotAvailableError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.OrderNotEmpty, (matchedCode) =>
            Effect.succeed(new OrderNotEmptyError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ProductVariantCodeNotExist, (matchedCode) =>
            Effect.succeed(new ProductVariantCodeNotExistError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ProductVariantNotExist, (matchedCode) =>
            Effect.succeed(new ProductVariantNotExistError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.ProductVariantNotSufficient, (matchedCode) =>
            Effect.succeed(new ProductVariantNotSufficientError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.OrderInvalidStateTransition, (matchedCode) =>
            Effect.succeed(new OrderInvalidStateTransitionError({ ...base, code: matchedCode }))
          ),
          Match.when(ViolationCode.OrderCartItemsImmutable, (matchedCode) =>
            Effect.succeed(new OrderCartItemsImmutableError({ ...base, code: matchedCode }))
          ),
          Match.orElse(() => Effect.succeed(new UnknownViolationError({ ...base, code })))
        );
      }),
      encode: SchemaGetter.forbidden(
        () => 'Encoding OrderViolationError back to API format is not supported'
      ),
    }
  )
);

/**
 * Decode an API violations array into a list of typed OrderViolationErrors.
 * Decoded as `ReadonlyArray<OrderViolationError>`; callers wrap in `Chunk`
 * when they need Chunk combinators.
 */
export const OrderViolationsFromApi = Schema.Array(OrderViolationFromApi);
